import { Component } from "@angular/core";
import { AuthService } from "./core/auth.service";
import { EMPTY, from } from "rxjs";
import { SocketMessagesService, TIncident } from "./core/socket/socket.service";
import { catchError, distinctUntilChanged, filter, map, switchMap, take, tap } from "rxjs/operators";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { ReconnectionDialogComponent } from "./core/reconnection-dialog/reconnection-dialog.component";
import { ActivatedRoute, NavigationEnd, Router } from "@angular/router";
import { AuthServerError } from "./types";
import { CommonService } from "./main/common.service";
import { CustomErrorHandlerService } from "./core/custom-error-handler.service";
import { environment } from "../environments/environment";
import { NotificationsService } from "./core/notifications/notifications.service";
import {IAuthResponse} from './core/user-info-storage.service';
import { SettingsService } from "./main/settings/settings.service";
import { getBackendBaseEndpoint } from "./main/utils";

function jsonFriendlyErrorReplacer(key: string, value: unknown) {
    if (value instanceof Error) {
        return {
            // Pull all enumerable properties, supporting properties on custom Errors
            ...value,
            // Explicitly pull Error's non-enumerable properties
            name: value.name,
            message: value.message,
            stack: value.stack,
        };
    }

    return value;
}

interface IJWTCallbackData {
    access_token: string;
    id_token: string;
    expires_in: number;
    token_type: string;
}

interface IErrorCallbackData {
    error_description: string;
    error: string;
}

@Component({
    selector: "sam4-root",
    styleUrls: ["app.component.scss"],
    templateUrl: "app.component.html",
    standalone: false
})
export class AppComponent {
    public  ai_media_contact_url = environment?.ai_media_contact_url || 'https://www.ai-media.tv/#start-a-conversation'
    public st!: SettingsService
    public now = Date.now();
    public this_user: IAuthResponse | undefined;
    constructor(
        private auth: AuthService,
        private messages: SocketMessagesService,
        private modal: NgbModal,
        private route: ActivatedRoute,
        private common: CommonService,
        private errors: CustomErrorHandlerService,
        private notificationsService: NotificationsService,
        private setting: SettingsService,
        router: Router,
    ) {
        router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(() => {
            const mainHeader = document.querySelector("main");
            if (mainHeader) {
                mainHeader.focus();
            }
            this.notificationsService.clear();
        });

        this.auth.isLoggedIn$.pipe(
            distinctUntilChanged(),
            filter(Boolean),
            tap(() => {
                this.common.reloadUsers();
                this.common.reloadBillingEntities();
                this.common.reloadOrganizations();
            }),
            tap(user => {return this.this_user = this.auth.getUserInfo()})
        ).subscribe();



        // Check IDAM auth
        this.route.fragment
            .pipe(
                filter(Boolean),
                map((_) => _ as string),
                map((str) => {
                    return str.split("&").reduce((previous: any, current) => {
                        const [key, value] = current.split("=");
                        previous[key] = value;
                        return previous;
                    }, {} as Partial<IJWTCallbackData | IErrorCallbackData>);
                }),
                map((payload: IJWTCallbackData | IErrorCallbackData) => {
                    const isError = (variableToCheck: any): variableToCheck is IErrorCallbackData =>
                        variableToCheck.error && variableToCheck.error_description;
                    if (isError(payload)) {
                        const errorMessage = decodeURIComponent(
                            (payload as IErrorCallbackData).error_description
                        ).replace(/\+/g, " ");
                        throw new AuthServerError(errorMessage);
                    }
                    return payload as IJWTCallbackData;
                }),
                switchMap(({ access_token, id_token, expires_in }) => {
                    return this.auth.postSSOLogin$(access_token, id_token, expires_in);
                }),
                take(1)
            )
            .subscribe();
        this.messages.incidents$
            .pipe(
                filter((incident) => (["re-connected", "connected"] as TIncident[]).includes(incident.type)),
                switchMap((incident) => this.auth.relogin$())
            )
            .subscribe();

        this.messages.incidents$
            .pipe(
                filter((i) => i.type === ("re-connecting" as TIncident)),
                switchMap(() => {
                    return from(
                        this.modal
                            .open(ReconnectionDialogComponent, {
                                size: "sm",
                                centered: true,
                            })
                            .result.catch(() => false)
                    );
                })
            )
            .subscribe();
        this.messages.incidents$
            .pipe(
                filter((i) => i.type === ("no-connection" as TIncident)),
                tap(() => {
                    throw new Error("Connection to server was lost and can't be established.");
                })
            )
            .subscribe();

        if (environment.logErrors) {
            this.errors.sendToBackend$
                .pipe(
                    switchMap((error) => {
                        const data = JSON.parse(JSON.stringify(error, jsonFriendlyErrorReplacer));
                        return this.messages.request$("logError", data).pipe(
                            catchError((internalError) => {
                                console.error(
                                    'Couldn\'t send error "%s" to backend due to another error: "%s"',
                                    error.message,
                                    internalError.message
                                );
                                return EMPTY;
                            })
                        );
                    })
                )
                .subscribe();
        }
    }

    getCheckPointUrl() {
        const base_url = getBackendBaseEndpoint('/v1/checkpoint')
        const user: any = this.auth.getUserInfo()
        const token = encodeURIComponent(user.bookeng_app_token || '')
        // Always send the currently-selected org_id. Omitting it makes the backend fall
        // back to the Ai-Media org, which surfaced the wrong KB after an org switch (the
        // org_has_knowledge_base flag is also updated asynchronously, so it can be stale here).
        return `${base_url}?token=${token}&org_id=${this.auth.curr_org_id}`
    }

    getKBFlag() {
        return this.setting.org_has_knowledge_base
    }

    getKBQryParams() {
        return { org_id: this.auth.curr_org_id }
    }

    isNotLoggedIn() {
        return !this.this_user
    }
}
